import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  Check, Crown, Video, MessageCircle, Shield, 
  Star, Upload, BarChart3, BadgeCheck, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierIcons = {
  premium: Crown,
  creator: Video,
  model: Star
};

const tierColors = {
  premium: 'from-gray-600 to-gray-800',
  creator: 'from-purple-600 to-purple-900',
  model: 'from-amber-500 to-amber-700'
};

const allFeatures = [
  { id: 'ad_free', label: 'Ad-Free Experience', icon: Shield },
  { id: 'watchlist', label: 'Unlimited Watchlist', icon: Check },
  { id: 'favorites', label: 'Unlimited Favorites', icon: Check },
  { id: 'hd_quality', label: 'HD Quality Streaming', icon: Check },
  { id: 'upload_videos', label: 'Upload Your Videos', icon: Upload },
  { id: 'messaging', label: 'Direct Messaging', icon: MessageCircle },
  { id: 'profile_page', label: 'Public Profile Page', icon: Check },
  { id: 'monetization', label: 'Monetize Content', icon: BarChart3 },
  { id: 'analytics', label: 'Advanced Analytics', icon: BarChart3 },
  { id: 'verified_badge', label: 'Verified Badge', icon: BadgeCheck },
];

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchTiers();
    checkPaymentStatus();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/subscription/tiers`);
      setTiers(response.data.tiers);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && isAuthenticated) {
      setCheckingPayment(true);
      try {
        // Poll for payment status
        for (let i = 0; i < 5; i++) {
          const response = await axios.get(
            `${API}/subscription/status/${sessionId}`,
            getAuthHeader()
          );
          
          if (response.data.payment_status === 'paid') {
            toast.success(`Welcome to ${response.data.tier}! Your subscription is now active.`);
            // Refresh user data
            window.location.href = '/';
            return;
          }
          
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        toast.error('Payment verification timed out. Please check your account.');
      } catch (error) {
        toast.error('Error verifying payment');
      } finally {
        setCheckingPayment(false);
      }
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    setProcessingTier(tier);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { tier, origin_url: window.location.origin },
        getAuthHeader()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingTier(null);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="payment-checking">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-2">Verifying Payment...</h2>
          <p className="text-gray-400">Please wait while we confirm your subscription</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-12" data-testid="subscription-page">
      <SEO 
        title="Premium Subscription" 
        description="Upgrade to VelvetStream Premium. Ad-free HD streaming, exclusive content, creator tools, and more starting at $9.99/month."
        path="/subscription"
      />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 
            className="text-5xl md:text-7xl font-normal tracking-wide uppercase mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Go Premium
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Unlock exclusive features, remove ads, and take your experience to the next level
          </p>
          {user?.subscription_tier && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
              <Crown className="w-4 h-4 text-amber-500" />
              <span className="text-white">Current plan: <strong className="capitalize">{user.subscription_tier}</strong></span>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {Object.entries(tiers).map(([key, tier]) => {
            const Icon = tierIcons[key] || Crown;
            const gradient = tierColors[key] || tierColors.premium;
            const isCurrentPlan = user?.subscription_tier === key;
            
            return (
              <div 
                key={key}
                className={`relative bg-[#121212] border rounded-lg overflow-hidden transition-all duration-300 hover:border-white/30 ${
                  key === 'creator' ? 'border-purple-500/50 scale-105 md:scale-110' : 'border-white/10'
                }`}
                data-testid={`tier-${key}`}
              >
                {key === 'creator' && (
                  <div className="absolute top-0 left-0 right-0 bg-purple-600 text-white text-center text-xs py-1 uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                
                <div className={`p-6 ${key === 'creator' ? 'pt-10' : ''}`}>
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Name & Price */}
                  <h3 className="text-2xl font-semibold text-white mb-2">{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">${tier.price}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {allFeatures.map(feature => {
                      const hasFeature = tier.features.includes(feature.id);
                      const FeatureIcon = feature.icon;
                      return (
                        <li 
                          key={feature.id}
                          className={`flex items-center gap-3 ${hasFeature ? 'text-white' : 'text-gray-600'}`}
                        >
                          <FeatureIcon className={`w-4 h-4 ${hasFeature ? 'text-green-500' : 'text-gray-700'}`} />
                          <span className="text-sm">{feature.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  
                  {/* CTA */}
                  <Button
                    onClick={() => handleSubscribe(key)}
                    disabled={processingTier === key || isCurrentPlan}
                    className={`w-full h-12 font-semibold tracking-wide uppercase ${
                      key === 'creator' 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                        : key === 'model'
                        ? 'bg-amber-500 hover:bg-amber-600 text-black'
                        : 'bg-white hover:bg-gray-200 text-black'
                    }`}
                    data-testid={`subscribe-${key}`}
                  >
                    {processingTier === key ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      `Get ${tier.name}`
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-3xl font-normal tracking-wide uppercase mb-8 text-center"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit cards through our secure Stripe payment processor.' },
              { q: 'How do I become a paid model?', a: 'Apply through our "Become a Model" page. If approved, you\'ll get the Model Pro tier with monetization features.' },
              { q: 'Is my payment information secure?', a: 'Absolutely. All payments are processed through Stripe with bank-level encryption.' },
            ].map((faq, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-lg p-5">
                <h4 className="text-white font-medium mb-2">{faq.q}</h4>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
